<template>
    <div
        :id="id"
        ref="wrapperContainer"
        class="tl-input-phone-number"
        :class="{
            'dropdown-opened': dropdownOpened,
        }"
    >
        <InputText
            :model-value="modelState"
            :label="label"
            :error="isPhoneValid ? error : t('input-phone-number.invalid')"
            :focus="focus"
            :disabled="disabled"
            :options="{ type: 'tel' }"
            @mounted="initTelInput"
            @update:model-value="handleUpdate"
        />
        <div
            ref="dropdownContainer"
            class="dropdown-container"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, watch, unref } from 'vue'
import { useI18n } from '../../utilities/i18n'
import useTools from 'widget-components/utilities/tools'
import intlTelInput from 'intl-tel-input'
import InputText from 'widget-components/components/InputText/InputText.vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { BlockPath, InputPhoneNumber, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import { isEmpty } from 'ramda'
import equals from 'fast-deep-equal/es6'
import { sentenceCase } from 'change-case'

const { t, locale } = useI18n()

const { wait } = useTools()

type ModelValueObj = { countryCode: string, number: string, fullNumber: string }

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    id?: string
    modelValue: string | ModelValueObj
    modelValueFormat?: 'string' | 'object'
    initialValue?: string | ModelValueObj
    label?: string
    disabled?: boolean
    focus?: boolean
    error?: string | boolean
    blockPath?: BlockPath
}>(), {
    modelValueFormat: 'string',
})

const emit = defineEmits(['update:modelValue', 'update:details', 'submit'])

const {
    requestBlockConfig,
    getLazyBlockProp,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const input = ref<HTMLElement | null>(null)
const telInput = ref<ReturnType<typeof intlTelInput> | null>(null)
const wrapperContainer = ref<HTMLElement | null>(null)
const dropdownContainer = ref(null)

const modelValueString = computed(() => {
    return props.modelValueFormat === 'string'
})

const modelState = ref<string>('')

const dropdownOpened = ref(false)
const label = computed(() => props.label)

const isPhoneValid = ref(true)

const getPhoneDetails = (val?: string) => {
    if (!telInput.value) return {}
    const fullNumber = val ?? telInput.value.getNumber()
    const countryDetails = telInput.value.getSelectedCountryData()
    const prefix = countryDetails.dialCode ? `+${countryDetails.dialCode}` : ''
    const countryName = countryDetails.name
    const countryCode = countryDetails.iso2
    const phoneNumber = fullNumber
        .replace(prefix, '')
        .replace('+', '')
    const isValid = fullNumber.length
        ? telInput.value.isValidNumber()
        : true

    return {
        phoneNumber,
        fullNumber,
        prefix,
        countryName,
        countryCode,
        isValid,
    }
}

const removeUnknownTooltip = async () => setTimeout(() => {
    const elem = wrapperContainer.value?.querySelector('.iti__selected-country')
    if (!elem) {
        return
    }
    const title = elem.getAttribute('title')

    if (title === 'Unknown') {
        elem.removeAttribute('title')
    }
}, 1000)

const handleUpdate = (val?: string) => {
    const details = getPhoneDetails(val)

    if (isEmpty(details) && !telInput.value) return

    const { fullNumber, isValid: valid } = details

    isPhoneValid.value = !!valid

    if (modelValueString.value) {
        emit('update:modelValue', fullNumber)
    } else {
        const value = {
            countryCode: details.prefix,
            number: details.phoneNumber,
            fullNumber: details.fullNumber,
        }
        emit('update:modelValue', value)
    }

    if (typeof fullNumber === 'string') {
        modelState.value = fullNumber
    }

    emit('update:details', details)
}

const initTelInput = async (inputElem: HTMLInputElement) => {
    if (!dropdownContainer.value) return

    const onlyCountries: string[] = []
    const localizedCountries: Record<string, string> = {}
    let initialCountry: string | undefined

    if (props.id) {
        await requestBlockConfig({ id: props.id, type: 'INPUT_PHONE_NUMBER' })
        const countries = getLazyBlockProp<InputPhoneNumber['selectedItems'] | undefined>(props.id, 'items')
        if (countries) {
            for (const country of countries) {
                if (country.selected) {
                    initialCountry = country.country
                }
                const countryCodeIso = country.country.toLowerCase()
                onlyCountries.push(countryCodeIso)
                localizedCountries[countryCodeIso] = country.label || sentenceCase(country.country)
            }
            if (!initialCountry && typeof props.initialValue === 'object') {
                initialCountry = countries.find(c => c.countryCode === Number((props.initialValue as ModelValueObj).countryCode))?.country
            }
        }
    }

    input.value = inputElem

    telInput.value = intlTelInput(inputElem, {
        dropdownContainer: dropdownContainer.value,
        loadUtils: () => import('intl-tel-input/utils'),
        onlyCountries,
        ...initialCountry
            ? { initialCountry }
            : {
                initialCountry: 'auto',
                geoIpLookup: callback => fetch(window.location.hostname === 'localhost'
                    ? 'https://ipinfo.io/json'
                    : 'https://ipinfo.io/json?token=2eb6b8d285d335',
                )
                    .then(res => res.json())
                    .then(data => callback(data.country))
                    .catch(() => callback(unref(locale))),
            },
        i18n: {
            searchPlaceholder: t('search'),
            ...localizedCountries,
        },
    })

    input.value.addEventListener('countrychange', async (event) => {
        const val = (event.target as HTMLInputElement)?.value
        handleUpdate(val)
    })

    input.value.addEventListener('open:countrydropdown', () => {
        const searchInput = document.querySelector('.iti__search-input') as HTMLInputElement
        if (searchInput) {
            searchInput.value = ''
            searchInput.dispatchEvent(new Event('input'))
        }

        dropdownOpened.value = true
        input.value?.scrollIntoView({
            block: 'nearest',
        })
    })

    input.value.addEventListener('close:countrydropdown', () => {
        dropdownOpened.value = false
    })

    await telInput.value.promise

    handleUpdate()
}

onMounted(async () => {
    if (modelValueString.value) {
        modelState.value = props.initialValue as string
    } else {
        modelState.value = (props.initialValue as ModelValueObj)?.number || ''
    }

    if (props.focus) {
        await wait(800)
        input.value?.focus()
    }

    removeUnknownTooltip()
})

watch(() => props.modelValue, () => {
    const val = modelValueString.value ? props.modelValue as string : (props.modelValue as ModelValueObj)?.number
    if (!equals(val, modelState.value) && telInput.value) {
        handleUpdate(val)
    }
})
</script>

<style scoped lang="sass">
@import './InputPhoneNumber.sass'
</style>
